import React, { useState, useEffect } from 'react'
import { supabase } from '../../../lib/supabase'

interface AnalyticsData {
  todayInspections: number
  weekProblems: number
  qualityRate: number
  totalProblems: number
  resolvedProblems: number
  lastUpdate: string
}

export const AnalyticsModule: React.FC = () => {
  const [data, setData] = useState<AnalyticsData>({
    todayInspections: 0,
    weekProblems: 0,
    qualityRate: 0,
    totalProblems: 0,
    resolvedProblems: 0,
    lastUpdate: ''
  })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadAnalyticsData()
  }, [])

  const loadAnalyticsData = async () => {
    try {
      setIsLoading(true)
      setError(null)
      
      console.log('🔄 Loading real analytics data from Supabase...')
      
      // Get today's date
      const today = new Date().toISOString().split('T')[0]
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      
      // Load today's inspections
      const { data: todayInspectionsData, error: inspectionsError } = await supabase
        .from('quality_control')
        .select('id, meets_requirements')
        .gte('created_at', `${today}T00:00:00Z`)
        .lte('created_at', `${today}T23:59:59Z`)

      if (inspectionsError) {
        console.error('❌ Error loading today\'s inspections:', inspectionsError)
        throw inspectionsError
      }

      // Load week's problems
      const { data: weekProblemsData, error: weekProblemsError } = await supabase
        .from('probleem_meldingen')
        .select('id, oplossing_gevonden')
        .gte('datum_tijd', `${weekAgo}T00:00:00Z`)

      if (weekProblemsError) {
        console.error('❌ Error loading week\'s problems:', weekProblemsError)
        throw weekProblemsError
      }

      // Load all problems for statistics
      const { data: allProblemsData, error: allProblemsError } = await supabase
        .from('probleem_meldingen')
        .select('id, oplossing_gevonden')

      if (allProblemsError) {
        console.error('❌ Error loading all problems:', allProblemsError)
        throw allProblemsError
      }

      // Calculate statistics
      const todayInspections = todayInspectionsData?.length || 0
      const weekProblems = weekProblemsData?.filter(p => !p.oplossing_gevonden).length || 0
      const totalProblems = allProblemsData?.length || 0
      const resolvedProblems = allProblemsData?.filter(p => p.oplossing_gevonden).length || 0
      
      // Calculate quality rate from today's inspections
      const passedInspections = todayInspectionsData?.filter(i => i.meets_requirements).length || 0
      const qualityRate = todayInspections > 0 ? Math.round((passedInspections / todayInspections) * 100) : 0

      const analyticsData: AnalyticsData = {
        todayInspections,
        weekProblems,
        qualityRate,
        totalProblems,
        resolvedProblems,
        lastUpdate: new Date().toLocaleString('nl-NL')
      }

      setData(analyticsData)
      console.log('✅ Analytics data loaded:', analyticsData)
    } catch (error) {
      console.error('❌ Error loading analytics data:', error)
      setError('Fout bij het laden van analytische gegevens')
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div className="text-center text-gray-500 py-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-kvh-red"></div>
        <p className="mt-2">Analytics laden...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center text-red-600 py-8">
        <p>{error}</p>
        <button 
          onClick={loadAnalyticsData}
          className="mt-4 px-4 py-2 bg-kvh-red text-white rounded hover:bg-kvh-dark-red transition-colors"
        >
          Opnieuw proberen
        </button>
      </div>
    )
  }

  return (
    <div className="module-content">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-800">📊 Kwaliteit Analytics</h2>
        <button 
          onClick={loadAnalyticsData}
          className="text-sm text-kvh-red hover:text-kvh-dark-red transition-colors"
        >
          🔄 Ververs
        </button>
      </div>
      
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <h3 className="font-semibold text-blue-800 mb-2">Vandaag</h3>
          <div className="text-2xl font-bold text-blue-600">{data.todayInspections}</div>
          <div className="text-sm text-gray-600">Inspecties uitgevoerd</div>
        </div>
        
        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
          <h3 className="font-semibold text-red-800 mb-2">Deze Week</h3>
          <div className="text-2xl font-bold text-red-600">{data.weekProblems}</div>
          <div className="text-sm text-gray-600">Open problemen</div>
        </div>
        
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <h3 className="font-semibold text-green-800 mb-2">Kwaliteit</h3>
          <div className="text-2xl font-bold text-green-600">{data.qualityRate}%</div>
          <div className="text-sm text-gray-600">Slagingspercentage</div>
        </div>

        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <h3 className="font-semibold text-gray-800 mb-2">Problemen</h3>
          <div className="text-2xl font-bold text-gray-600">{data.resolvedProblems}/{data.totalProblems}</div>
          <div className="text-sm text-gray-600">Opgelost/Totaal</div>
        </div>
      </div>
      
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="font-semibold text-gray-800 mb-4">📈 Gegevens Overzicht</h3>
        <div className="space-y-3">
          <div className="flex justify-between items-center py-2 border-b border-gray-200">
            <span className="text-gray-600">Totaal inspecties vandaag:</span>
            <span className="font-semibold">{data.todayInspections}</span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-gray-200">
            <span className="text-gray-600">Open problemen deze week:</span>
            <span className="font-semibold text-red-600">{data.weekProblems}</span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-gray-200">
            <span className="text-gray-600">Opgeloste problemen:</span>
            <span className="font-semibold text-green-600">{data.resolvedProblems}</span>
          </div>
          <div className="flex justify-between items-center py-2">
            <span className="text-gray-600">Laatst bijgewerkt:</span>
            <span className="font-semibold text-sm">{data.lastUpdate}</span>
          </div>
        </div>
      </div>
    </div>
  )
}